import _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

import withPusherBind from "UIComponents/PusherConsumerHoc";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { useI18n } from "Hooks";

import { EmptyState } from "@toddle-edu/ds-web";
import { NoNotificationIllustration } from "@toddle-edu/ds-theme";

import {
  getUserId,
  getUserEntityType,
  getUserType,
  getSelectedCurriculumId,
  getOrganizationId,
} from "modules/CommonSelectors";
import {
  getNotificationTypeObj,
  getUserNotificationCommonCourseIdsMemoize,
} from "../../modules/Utils";

import { notificationCenterOpenMutation } from "AppComponents/NotificationCenterV2/modules/NotificationCenterV2Modules";
import { setCurrentCurriculumProgram } from "Platform/modules/PlatformModule";
import { navigate } from "modules/NavigationModule";
import { getUserCoursesBasicDetails } from "Course/modules/CourseModule";
import { getBehaviourNomenclatureFromCache } from "BehaviourConfiguration/modules/BehaviourConfigurationGraphqlHelpers";
import {
  getNewValueWhenGlobalHeaderEnabled,
  notificationReadOnClick,
} from "../../modules/NotificationCenterV2Modules";

import { getNotificationsEnhancer } from "../../modules/NotificationCenterV2Enhancer";

import NotificationCard from "../NotificationCard";
import classes from "./NotificationList.scss";
import { useIntersectionObserver } from "Utils";

const styles = {
  loadingStyle: { width: 28, height: 28 },
};

const NotificationList = React.memo(props => {
  const {
    onNotificationClicked,
    notifications,
    userType,
    testId,
    behaviourNomenclatureConstants,
  } = props;
  const notificationItemRefs = React.useRef([]);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (selectedItem != null) setSelectedItem(null);
  }, [notifications]);
  useEffect(() => {
    if (notificationItemRefs.current[selectedItem] && selectedItem !== null) {
      notificationItemRefs.current[selectedItem].focus();
    }
  }, [selectedItem]);

  return _.map(notifications, (notificationItem, index) => {
    const notificationType = _.get(notificationItem, "type", "");
    const notificationDetails = getNotificationTypeObj({ notificationType });
    return (
      <NotificationCard
        key={notificationItem.id}
        data={notificationItem}
        tabIndex={getNewValueWhenGlobalHeaderEnabled("0", -1)}
        notificationDetails={notificationDetails}
        onNotificationClicked={onNotificationClicked}
        userType={userType}
        currentIdx={index}
        ref={notificationItemRefs}
        testId={`${testId}-notificationListItem-${index}-notificationCard`}
        behaviourNomenclatureConstants={behaviourNomenclatureConstants}
        setSelectedItem={setSelectedItem}
        totalNotificationCount={notifications.length}
        selectedItem={selectedItem}
      />
    );
  });
});

NotificationList.displayName = "NotificationList";

const observerParams = { threshold: 0.8 };
const NotificationListContainer = React.memo(props => {
  const {
    userId,
    userType,
    currentCurriculumProgramId,
    activeTabFilterValue,
    notificationListRef,
    notifications,
    hasNextPage,
    networkStatus,
    isFetchingMoreNotifications,
    loadMoreNotifications,
    notificationCenterOpenMutation,
    setCurrentCurriculumProgram,
    closeNotificationCenter,
    navigate,
    notificationReadOnClick,
    courses,
    selectedCourseId,
    isFromSideBar,
    getUserCoursesBasicDetails,
    toggleShowNotificationCenter,
    testId,
    behaviourNomenclatureConstants,
    isLoading,
  } = props;

  const { t } = useI18n(["notifications", "announcement"]);
  const [isUserCoursesLoading, setIsUserCoursesLoading] = useState(false);
  // The loader anchor is rendered only when hasNextPage is true. The shared
  // useIntersectionObserver attaches once and won't pick up a target that
  // mounts later, so we track the element in state and feed the hook a fresh
  // ref-shaped object on each mount/unmount.
  const [targetEl, setTargetEl] = useState(null);
  const targetRefObj = useMemo(() => ({ current: targetEl }), [targetEl]);
  const isInView = useIntersectionObserver(targetRefObj, observerParams);

  useEffect(() => {
    return () => {
      notificationCenterOpenMutation({
        userId,
        activeTabFilterValue,
      });
    };
  }, [activeTabFilterValue]);

  const getCoursesForUnitPlanNotification = async notification => {
    // find notification curriculum program
    const notificationCourse = _.head(_.get(notification, "courses", []));
    const notificationCourseId = _.get(notificationCourse, "id", null);

    let notificationCurriculumProgramId = _.get(
      notificationCourse,
      "curriculumProgram.id",
      null
    );
    if (!notificationCurriculumProgramId) {
      notificationCurriculumProgramId = _.get(
        notification,
        "metadata.curriculumProgramId"
      );
    }

    let courseIdToRedirect;
    let courseToRedirect;
    let isUnitLibraryRoute = false;
    // fetch user's courses for the notification curriculum program
    setIsUserCoursesLoading(true);
    const otherCurriculumProgramCourses = await getUserCoursesBasicDetails({
      userId,
      courseFilters: {
        curriculumProgramIds: [notificationCurriculumProgramId],
        archivalState: "ACTIVE",
      },
    });
    setIsUserCoursesLoading(false);
    // use above fetched courses to find single course to redirect
    const notificationCurriculumProgramCourses = otherCurriculumProgramCourses;

    const isStaffInCourse = !_.isEmpty(
      _.find(notificationCurriculumProgramCourses, {
        id: notificationCourseId,
      })
    );

    if (isStaffInCourse) {
      // if staff is part of the notification course, then redirect to that course
      courseToRedirect = notificationCourse;
      courseIdToRedirect = notificationCourseId;
    } else {
      // if staff is not a part of the notification course,
      // then redirect to the first course from user's courses inside notification curriculum program
      courseToRedirect = _.head(notificationCurriculumProgramCourses);
      courseIdToRedirect = _.get(courseToRedirect, "id");
      isUnitLibraryRoute = true;
    }
    return { courseIdToRedirect, courseToRedirect, isUnitLibraryRoute };
  };

  // function to switch curriculum if notification is of other curriculum
  const syncPlatformCurriculum = ({ course }) => {
    const notificationCurriculumProgram = _.get(
      course,
      "curriculumProgram",
      {}
    );

    if (
      !_.isEmpty(notificationCurriculumProgram) &&
      currentCurriculumProgramId !== notificationCurriculumProgram.id
    ) {
      setCurrentCurriculumProgram(notificationCurriculumProgram);
    }
  };

  const getNotificationCourseToRedirect = async notification => {
    const notificationType = _.get(notification, "type", "");

    // edge case for notification type "COLLABORATOR"
    // remove below code on future if unit library moves from class to home page
    if (
      _.includes(
        ["COLLABORATOR", "COMMENT_UNIT_PLAN", "COMMENT_ASSESSMENT"],
        notificationType
      )
    ) {
      return await getCoursesForUnitPlanNotification(notification);
    }

    // current user curriculum course ids
    const userCourseIds = _.map(courses, course => _.get(course, "id"));

    // if notification is having multiple courses
    const notificationCourseIds = _.get(notification, "metadata.courses");

    // if notification is having single course, then one of the below two will contain that course id
    const notificationCourseId = _.get(notification, "metadata.courseId");
    const notificationCourse_id = _.get(notification, "metadata.course_id");

    // if notification center is opened inside any course
    const userSelectedCourseId = isFromSideBar ? selectedCourseId : "";

    const commonCourseIds = getUserNotificationCommonCourseIdsMemoize({
      userCourseIds,
      notificationCourseIds,
    });

    // choose course id to redirect when notification is clicked
    const courseIdToRedirect =
      _.first(commonCourseIds) ||
      userSelectedCourseId ||
      notificationCourseId ||
      notificationCourse_id ||
      _.head(userCourseIds);

    const courseToRedirect = _.find(
      _.get(notification, "courses", []),
      course => course.id == courseIdToRedirect
    );

    return { courseIdToRedirect, courseToRedirect };
  };

  const onNotificationClicked = async notification => {
    //Over riding user courseId in case of notification having multiple course.
    const cloneNotification = _.cloneDeep(notification);

    // current user curriculum course ids
    const userCourseIds = _.map(courses, course => _.get(course, "id"));

    const {
      courseIdToRedirect,
      courseToRedirect,
      isUnitLibraryRoute = false,
    } = await getNotificationCourseToRedirect(notification);

    cloneNotification.metadata = {
      ...(cloneNotification.metadata || {}),
      courseId: courseIdToRedirect,
      userCourseIds,
      isUnitLibraryRoute,
    };

    // mark notification as read
    notificationReadOnClick({
      notificationId: cloneNotification.id,
    });

    if (!_.isEmpty(courseToRedirect)) {
      syncPlatformCurriculum({ course: courseToRedirect });
    }

    navigate({
      params: cloneNotification,
      target: "notification",
    });

    // close notification center if opened from sidebar inside course
    if (closeNotificationCenter) {
      closeNotificationCenter();
    }

    // close notification center if opened from notification tray
    if (toggleShowNotificationCenter) {
      toggleShowNotificationCenter();
    }
  };
  const renderNotifications = () => {
    return (
      <div className={classes.notificationsContainer}>
        <NotificationList
          notifications={notifications}
          userType={userType}
          onNotificationClicked={onNotificationClicked}
          testId={testId}
          behaviourNomenclatureConstants={behaviourNomenclatureConstants}
        />
        {/* this condition is added  to avoid overfetching  on scroll */}
        {hasNextPage ? (
          <div className={classes.loadingContainer} ref={setTargetEl}>
            <Loading loadingStyle={styles.loadingStyle} />
          </div>
        ) : null}

        {!hasNextPage && (
          <div className={classes.noMoreUnreadNotification}>
            <div className={classes.emoji}>{"🙌"}</div>
            <div>{t("announcement:hurray_that_was_the_last_notification")}</div>
          </div>
        )}
      </div>
    );
  };

  const renderEmptyState = () => {
    return (
      <div className={classes.emptyState}>
        <EmptyState
          illustration={NoNotificationIllustration}
          subtitle={t("announcement:its_a_quiet_day_in_notification_land")}
          subtitleStyle={{
            width: "272px",
          }}
        />
      </div>
    );
  };

  const loadMoreNotificationDepRef = useRef({
    isLoading,
    isFetchingMoreNotifications,
    hasNextPage,
    loadMoreNotifications,
  });

  loadMoreNotificationDepRef.current = {
    isLoading,
    isFetchingMoreNotifications,
    hasNextPage,
    loadMoreNotifications,
  };

  useEffect(() => {
    const {
      isLoading,
      isFetchingMoreNotifications,
      hasNextPage,
      loadMoreNotifications,
    } = loadMoreNotificationDepRef.current;
    if (isInView && hasNextPage && !isLoading && !isFetchingMoreNotifications) {
      loadMoreNotifications();
    }
  }, [isInView]);

  return (
    <div className={classes.container} ref={notificationListRef}>
      {!_.isEmpty(notifications) ? renderNotifications() : renderEmptyState()}
      {isUserCoursesLoading ? <FullScreenLoader /> : null}
    </div>
  );
});

NotificationListContainer.displayName = "NotificationListContainer";

NotificationListContainer.propTypes = {};

const mapActionCreators = {
  notificationCenterOpenMutation,
  notificationReadOnClick,
  setCurrentCurriculumProgram,
  navigate,
  getUserCoursesBasicDetails,
};

const mapStateToProps = (state, ownProps) => {
  const { showOnlyUnread } = ownProps;
  const userId = getUserId(state);
  const userType = getUserType(state);
  const organizationId = getOrganizationId(state);
  const userEntityType = getUserEntityType(state);
  const currentCurriculumProgramId = getSelectedCurriculumId(state);
  const activeTabFilterValue = _.get(ownProps, "activeTabFilterValue");
  const behaviourNomenclatureConstants = getBehaviourNomenclatureFromCache({
    organizationId,
  });

  return {
    variables: {
      id: userId,
      type: userEntityType,
      filters: {
        showOnlyUnread,
      },
      notificationCategoryFilter: activeTabFilterValue,
    },
    userId,
    userType,
    activeTabFilterValue,
    currentCurriculumProgramId,
    behaviourNomenclatureConstants,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  getNotificationsEnhancer(),
  withPusherBind(["notificationAdded"]),
  withLoader
)(NotificationListContainer);
